package fr.manyverse.app.presentation.components.player_screen.inventory

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.FilterChip
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.InventoryItem
import fr.manyverse.app.domain.model.ItemCategory
import fr.manyverse.app.domain.model.TableMember
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.inventory_give_button
import manyverse.shared.generated.resources.inventory_give_no_recipients
import manyverse.shared.generated.resources.inventory_give_quantity
import manyverse.shared.generated.resources.inventory_give_title
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun GiveInventoryItemDialog(
    showDialog: Boolean,
    item: InventoryItem,
    displayQuantity: Int,
    recipients: List<TableMember>,
    characters: List<Character>,
    isLoading: Boolean,
    errorMessage: String?,
    onDismiss: () -> Unit,
    onConfirm: (toCharacterId: String, quantity: Int) -> Unit,
    modifier: Modifier = Modifier
) {
    if (!showDialog) return

    var selectedCharacterId by remember(showDialog) { mutableStateOf("") }
    var quantity by remember(showDialog) { mutableIntStateOf(1) }

    val isConsumable = item.category == ItemCategory.CONSUMABLE
    val canSubmit = selectedCharacterId.isNotBlank() && !isLoading

    AlertDialog(
        onDismissRequest = { if (!isLoading) onDismiss() },
        title = { Text(stringResource(Res.string.inventory_give_title)) },
        text = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                if (recipients.isEmpty()) {
                    Text(
                        text = stringResource(Res.string.inventory_give_no_recipients),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                } else {
                    FlowRow(
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                        modifier = Modifier.padding(top = 4.dp)
                    ) {
                        recipients.forEach { member ->
                            val characterId = member.characterId ?: return@forEach
                            FilterChip(
                                selected = characterId == selectedCharacterId,
                                onClick = { selectedCharacterId = characterId },
                                label = { Text(characters.find { it.userId == member.userId }?.name ?: characterId.take(8)) },
                                enabled = !isLoading
                            )
                        }
                    }
                }

                if (isConsumable) {
                    Column {
                        Text(
                            text = stringResource(Res.string.inventory_give_quantity),
                            style = MaterialTheme.typography.labelMedium
                        )
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            modifier = Modifier.padding(top = 4.dp)
                        ) {
                            IconButton(
                                onClick = { if (quantity > 1) quantity-- },
                                enabled = quantity > 1 && !isLoading
                            ) {
                                Text("−", style = MaterialTheme.typography.titleMedium)
                            }
                            Text(
                                text = quantity.toString(),
                                style = MaterialTheme.typography.bodyLarge,
                                modifier = Modifier.padding(horizontal = 4.dp)
                            )
                            IconButton(
                                onClick = { if (quantity < displayQuantity) quantity++ },
                                enabled = quantity < displayQuantity && !isLoading
                            ) {
                                Text("+", style = MaterialTheme.typography.titleMedium)
                            }
                        }
                    }
                }

                if (errorMessage != null) {
                    Text(
                        text = errorMessage,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall
                    )
                }

                if (isLoading) {
                    LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
                }
            }
        },
        confirmButton = {
            Button(
                onClick = { onConfirm(selectedCharacterId, quantity) },
                enabled = canSubmit
            ) {
                Text(stringResource(Res.string.inventory_give_button))
            }
        },
        dismissButton = {
            TextButton(
                onClick = onDismiss,
                enabled = !isLoading
            ) {
                Text(stringResource(Res.string.cancel))
            }
        },
        modifier = modifier
    )
}
